import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, Delta, EmptyState, LiveBadge } from "@/components/common";
import { DonutAllocation, AreaTrend } from "@/components/Charts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { fmtUSD, fmtCompact, fmtCcy, fmtUSDStrict, timeAgo } from "@/lib/format";
import { fmtDisplay } from "@/lib/fx";
import { CurrencyToggle } from "@/components/CurrencyToggle";
import { PortfolioTicker } from "@/components/PortfolioTicker";
import { CountUp } from "@/components/CountUp";
import { toastError } from "@/lib/toastError";
import { ShariahWatchCard } from "@/components/halal/ShariahWatchCard";
import { useNavigate } from "react-router-dom";
import { Vault, PieChart, Globe, Building2, Lightbulb, ArrowRight, Activity, Moon, ArrowDownLeft, ArrowUpRight, Clock } from "lucide-react";

const MODULE_LABELS = { invest: "Invest", trade_global: "Trade Global", trade_gcc: "Trade GCC", save: "Savings", halal: "Halal", wallet: "Wallet" };

const GOAL_LABELS = { grow: "grow your wealth", retirement: "save for retirement", income: "generate passive income", halal: "invest the halal way", purchase: "reach a big goal", education: "build an education fund" };
const RISK_REC = {
  conservative: { name: "Conservative Income", mix: "20% equities · 70% sukuk & bonds · 10% cash", tone: "Capital preservation with steady, lower-volatility returns.", rate: 0.045 },
  balanced: { name: "Balanced Growth", mix: "60% global equities · 35% sukuk & bonds · 5% cash", tone: "A diversified blend of growth and stability.", rate: 0.075 },
  aggressive: { name: "Aggressive Growth", mix: "90% global & emerging equities · 10% alternatives", tone: "Maximised long-term growth for a longer horizon.", rate: 0.10 },
};

export default function Dashboard() {
  const { user, t, displayCcy } = useApp();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [d, a] = await Promise.all([api.get("/dashboard"), api.get("/activity")]);
      setDash(d.data); setActivity(a.data);
    } catch (e) {
      // Dashboard polls on an interval — dedupe repeated failures with a stable id.
      toastError("Couldn't refresh your dashboard", e, { id: "dashboard-load", fallback: "We'll keep retrying." });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); }, []);

  const breakdown = dash?.breakdown || {};
  const onb = user?.onboarding || null;
  const rec = onb ? (RISK_REC[onb.risk] || RISK_REC.balanced) : null;
  const firstName = (user?.name || "").split(" ")[0] || "";
  const principal = Number(onb?.invest_amount) || 10000;
  const projection = rec ? Array.from({ length: 11 }, (_, y) => ({
    date: `Y${y}`, value: Math.round(principal * Math.pow(1 + rec.rate, y)),
  })) : [];
  const projected10 = projection.length ? projection[projection.length - 1].value : 0;
  const kycPending = ["in_review", "pending_review"].includes(user?.kyc?.status);
  const donutData = Object.entries(breakdown).filter(([, v]) => v > 0).map(([k, v]) => ({ name: MODULE_LABELS[k], value: Number(v) }));

  return (
    <AppShell>
      <PageHeader title={`${t("nav_dashboard")}`} subtitle={`Welcome back, ${user?.name || ""}`}
        action={<div className="flex flex-wrap items-center gap-2">
          <CurrencyToggle />
          <Button variant="secondary" onClick={() => navigate("/wallet")} data-testid="quick-deposit">{t("deposit")}</Button>
          <Button onClick={() => navigate("/invest")} data-testid="quick-invest">{t("invest_now")}</Button>
        </div>} testid="dashboard-header" />

      <PortfolioTicker totalUsd={dash?.total_value_usd} />

      {kycPending && (
        <Card className="p-4 mb-4 sm:mb-5 border-0 shadow-premium flex items-center gap-3 bg-[#FFF7E6]" data-testid="dashboard-kyc-pending">
          <div className="h-9 w-9 rounded-xl bg-[#6E8524]/15 flex items-center justify-center shrink-0"><Clock className="h-4 w-4 text-[#8a6a1f]" /></div>
          <div className="text-sm text-[#5c4711]">
            <span className="font-semibold">Your identity is under review.</span> Most checks finish within a few minutes — we'll email you as soon as you're verified.
          </div>
        </Card>
      )}

      {onb && rec && (
        <Card className="relative overflow-hidden p-5 sm:p-6 mb-4 sm:mb-5 border-0 shadow-premium hero-sheen" data-testid="dashboard-recommendation">
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wide text-[color:var(--tharwy-lime-700)] font-semibold">Recommended for you</div>
              <h3 className="mt-1 text-xl font-semibold">
                {firstName ? `${firstName}, ` : ""}based on your goal to {GOAL_LABELS[onb.goal] || "invest"}, we suggest the <span className="text-[color:var(--tharwy-lime-700)]">{rec.name}</span> portfolio
              </h3>
              <p className="text-sm text-muted-foreground mt-1.5">{rec.tone}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="secondary" className="capitalize">Risk: {onb.risk || "balanced"}</Badge>
                <Badge variant="secondary">{rec.mix}</Badge>
                {onb.products?.includes("halal") && <Badge variant="secondary" className="gap-1"><Moon className="h-3 w-3" /> Shariah-screened</Badge>}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="lg" variant="outline" className="gap-1.5" data-testid="recommendation-preview">
                    <Activity className="h-4 w-4" /> Preview growth
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg" data-testid="recommendation-preview-dialog">
                  <DialogHeader><DialogTitle>{rec.name} — projected growth</DialogTitle></DialogHeader>
                  <div className="text-sm text-muted-foreground -mt-1">
                    Projected value of {fmtUSD(principal)} at an assumed {(rec.rate * 100).toFixed(1)}% annual return.
                  </div>
                  <div className="rounded-xl bg-secondary/50 p-3 flex items-center justify-between">
                    <span className="text-sm">Est. value in 10 years</span>
                    <span className="numeric text-lg font-semibold text-[color:var(--tharwy-lime-700)]">{fmtUSD(projected10)}</span>
                  </div>
                  <AreaTrend data={projection} dataKey="value" xKey="date" height={220} />
                  <p className="text-[11px] text-muted-foreground">Illustrative only. Projections are not guarantees; investing involves risk of loss.</p>
                </DialogContent>
              </Dialog>
              <Button size="lg" className="gap-1.5" onClick={() => navigate("/invest")} data-testid="recommendation-cta">
                Build this portfolio <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-3 mb-4 sm:mb-5">
        {[
          { title: "Managed Investing", desc: "Ready-made ETF portfolios, built & rebalanced for you", to: "/invest", icon: PieChart, cta: "Start investing" },
          { title: "Halal Discover", desc: "Screen 40+ stocks & ETFs for Shariah compliance", to: "/halal", icon: Moon, cta: "Explore halal" },
          { title: "Self-Directed", desc: "Trade US & GCC stocks yourself, fractional shares", to: "/trade-global", icon: Globe, cta: "Start trading" },
        ].map((c) => (
          <Card key={c.title} className="p-5 shadow-premium hover:-translate-y-0.5 transition-transform cursor-pointer group" onClick={() => navigate(c.to)} data-testid={`grow-card-${c.to.replace("/", "")}`}>
            <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center mb-3 group-hover:bg-[color:var(--tharwy-lime)]/15 transition-colors"><c.icon className="h-5 w-5 text-primary" /></div>
            <div className="font-semibold">{c.title}</div>
            <div className="text-sm text-muted-foreground mt-1 mb-3">{c.desc}</div>
            <span className="text-sm font-medium text-[color:var(--tharwy-lime-700)] inline-flex items-center gap-1">{c.cta} <ArrowRight className="h-4 w-4" /></span>
          </Card>
        ))}
      </div>


      <div className="grid gap-4 sm:gap-5 lg:grid-cols-4">
        <Card className="balance-hero p-5 sm:p-6 lg:col-span-2 rounded-2xl border-0 shadow-[0_26px_70px_-42px_rgba(62,46,101,0.55)]" data-testid="dashboard-total-portfolio">
          <div className="relative z-10 text-[#1A1330]">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#1A1330]/70">{t("total_portfolio")}</span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A1330]">
                <span className="h-2 w-2 rounded-full bg-[#1F1C52] live-dot" /> LIVE
              </span>
            </div>
            <CountUp value={dash?.total_value_usd} className="mt-2 block text-4xl sm:text-5xl font-bold numeric"
              testid="dashboard-total-value" format={(v) => fmtDisplay(v, displayCcy)} />
            <div className="mt-1 text-sm text-[#1A1330]/70">Across Wallet, Savings &amp; Investments</div>

            <div className="mt-5 flex flex-wrap gap-2.5">
              <Button onClick={() => navigate("/wallet")} data-testid="balance-card-deposit-button"
                className="rounded-full bg-white text-[#332654] hover:bg-white/90 h-10 px-4 gap-1.5 shadow-none">
                <ArrowDownLeft className="h-4 w-4" /> {t("deposit")}
              </Button>
              <Button onClick={() => navigate("/wallet")} data-testid="balance-card-send-button"
                className="rounded-full bg-[#1F1C52] text-white hover:bg-[#332654] h-10 px-4 gap-1.5 shadow-none">
                <ArrowUpRight className="h-4 w-4" /> Send
              </Button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2.5">
              {Object.entries(breakdown).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between rounded-xl bg-white/55 border border-white/60 backdrop-blur-sm px-3 py-2">
                  <span className="text-sm text-[#1A1330]/75">{MODULE_LABELS[k]}</span>
                  <span className="numeric text-sm font-semibold text-[#1A1330]">{fmtDisplay(v, displayCcy)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card className="p-5 shadow-premium">
          <div className="text-sm font-medium mb-2">Allocation</div>
          {donutData.length ? <DonutAllocation data={donutData} height={200} /> :
            <EmptyState title={t("no_data")} hint="Start investing to see your allocation" testid="dashboard-allocation-empty" />}
          <div className="mt-2 space-y-1" data-testid="dashboard-allocation-donut">
            {donutData.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{d.name}</span>
                <span className="numeric">{fmtDisplay(d.value, displayCcy)}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-4 sm:space-y-5">
          <Card className="p-4 shadow-premium">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Wallet</div>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">USD</span><span className="numeric font-medium">{fmtUSDStrict(dash?.wallet?.USD)}</span></div>
              <div className="flex justify-between"><span className="text-sm text-muted-foreground">QAR</span><span className="numeric font-medium">{fmtCcy(dash?.wallet?.QAR, "QAR")}</span></div>
            </div>
            <Button variant="secondary" size="sm" className="w-full mt-3" onClick={() => navigate("/wallet")}>Manage Wallet</Button>
          </Card>
          <Card className="p-4 shadow-premium">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t("quick_actions")}</div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => navigate("/trade-global")}><Globe className="h-4 w-4" />Global</Button>
              <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => navigate("/trade-gcc")}><Building2 className="h-4 w-4" />GCC</Button>
              <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => navigate("/save")}><Vault className="h-4 w-4" />Save</Button>
              <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => navigate("/halal")}>Halal</Button>
            </div>
          </Card>
        </div>
      </div>

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3 mt-4 sm:mt-5">
        <Card className="p-5 shadow-premium lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium">{t("market_snapshot")}</div>
          </div>
          <Tabs defaultValue="US">
            <TabsList><TabsTrigger value="US">US Markets</TabsTrigger><TabsTrigger value="GCC">GCC Markets</TabsTrigger></TabsList>
            {["US", "GCC"].map((region) => (
              <TabsContent key={region} value={region} className="mt-3">
                <div className="grid sm:grid-cols-2 gap-2">
                  {(dash?.snapshot?.[region] || []).map((s) => (
                    <div key={`snap-${s.symbol}`} className="flex items-center justify-between rounded-lg border px-3 py-2 hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>
                      <div><div className="font-medium text-sm">{s.symbol}</div><div className="text-xs text-muted-foreground truncate max-w-[160px]">{s.name}</div></div>
                      <div className="text-right"><div className="numeric text-sm">{fmtCcy(s.price, s.currency)}</div><Delta value={s.change_pct} className="text-xs" /></div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </Card>

        <div className="space-y-4 sm:space-y-5">
          <ShariahWatchCard />
          <Card className="p-5 shadow-premium">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><Lightbulb className="h-4 w-4 text-[color:var(--tharwy-lime)]" />{t("insights")}</div>
            <div className="space-y-2">
              {(dash?.insights || []).map((ins, i) => (
                <div key={ins.title ?? i} className="rounded-lg bg-secondary/60 p-3">
                  <div className="text-sm font-medium">{ins.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{ins.message}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-5 shadow-premium mt-4 sm:mt-5">
        <div className="flex items-center gap-2 text-sm font-medium mb-3"><Activity className="h-4 w-4" />{t("recent_activity")}</div>
        {activity.length ? (
          <div className="divide-y" data-testid="dashboard-activity-feed">
            {activity.slice(0, 8).map((a, i) => (
              <div key={a.id || `${a.timestamp}-${a.type}-${i}`} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="capitalize">{(a.type || "").replace("_", " ")}</Badge>
                  <span className="text-sm">{a.symbol || a.portfolio || a.from ? `${a.symbol || a.portfolio || ""}${a.from ? `${a.from}→${a.to}` : ""}` : a.method || ""}</span>
                </div>
                <div className="text-right"><div className="numeric text-sm">{fmtCcy(a.amount, a.currency || "USD")}</div><div className="text-xs text-muted-foreground">{timeAgo(a.timestamp)}</div></div>
              </div>
            ))}
          </div>
        ) : <EmptyState title="No activity yet" hint="Your deposits, trades and transfers will appear here" />}
      </Card>
    </AppShell>
  );
}
